import { alphaSort, getLocalName, numericSort } from 'vet-bones/utils';

import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import {
  LabelSuggestion,
  PrimaryIdentifierSuggestion,
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import {
  IgnoredSuggestion,
  getOrganizedSuggestionRecords,
  getResourceSuggestionKey,
} from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';

const findBestPrimaryIdentifierMatch = (
  primaryIdentifierSuggestions: Suggestion[],
  classIri: string
): PrimaryIdentifierSuggestion | null => {
  if (!primaryIdentifierSuggestions.length) {
    return null;
  }

  const className = getLocalName(classIri);

  const primaryIdentifiers = primaryIdentifierSuggestions.filter(
    (suggestion): suggestion is PrimaryIdentifierSuggestion =>
      suggestion.type === SuggestionType.PRIMARY_IDENTIFIER
  );

  if (!primaryIdentifiers.length) {
    return null;
  }

  const exactMatch = primaryIdentifiers.find((suggestion) => {
    const fieldName = suggestion.fields[0]?.value || '';

    return (
      fieldName.toLowerCase().includes(className.toLowerCase()) ||
      className
        .toLowerCase()
        .includes(fieldName.toLowerCase().replace(/\s+id$/i, ''))
    );
  });
  if (exactMatch) {
    return exactMatch;
  }

  const partialMatch = primaryIdentifiers.find((suggestion) => {
    const fieldName = suggestion.fields[0]?.value || '';

    const fieldWords = fieldName.toLowerCase().split(/[\s_]+/);
    const classWords = className.toLowerCase().split(/(?=[A-Z])/);

    return fieldWords.some((fieldWord: string) =>
      classWords.some(
        (classWord: string) =>
          fieldWord.includes(classWord) || classWord.includes(fieldWord)
      )
    );
  });
  if (partialMatch) {
    return partialMatch;
  }

  return primaryIdentifiers[0];
};

const findBestLabelMatch = (
  labelSuggestions: Suggestion[],
  classIri: string
): LabelSuggestion | null => {
  if (!labelSuggestions.length) {
    return null;
  }

  const className = getLocalName(classIri);

  const labels = labelSuggestions.filter(
    (suggestion): suggestion is LabelSuggestion =>
      suggestion.type === SuggestionType.LABEL
  );

  if (!labels.length) {
    return null;
  }

  const exactMatch = labels.find((suggestion) => {
    const fieldName = suggestion.field || '';

    return (
      fieldName.toLowerCase().includes(className.toLowerCase()) ||
      className
        .toLowerCase()
        .includes(
          fieldName.toLowerCase().replace(/\s+(name|label|title)$/i, '')
        )
    );
  });
  if (exactMatch) {
    return exactMatch;
  }

  const partialMatch = labels.find((suggestion) => {
    const fieldName = suggestion.field || '';

    const fieldWords = fieldName.toLowerCase().split(/[\s_]+/);
    const classWords = className.toLowerCase().split(/(?=[A-Z])/);

    return fieldWords.some((fieldWord: string) =>
      classWords.some(
        (classWord: string) =>
          fieldWord.includes(classWord) || classWord.includes(fieldWord)
      )
    );
  });
  if (partialMatch) {
    return partialMatch;
  }

  return labels[0];
};

export const getMappingSuggestions = ({
  labelsByIri,
  mappings,
  selectedClasses,
  suggestions,
}: {
  labelsByIri: Record<string, string>;
  mappings: Mapping[];
  selectedClasses: string[];
  suggestions: Suggestion[];
}): {
  ignoredSuggestions: IgnoredSuggestion[];
  mappingSuggestions: MappingSuggestion[];
} => {
  const isClassAutoCreated = selectedClasses.length === 0;

  const {
    classSuggestions,
    attributeSuggestions,
    relationshipSuggestions,
    labelSuggestions,
    primaryIdentifierSuggestions,
    ignoredSuggestions,
  } = getOrganizedSuggestionRecords({
    isClassAutoCreated,
    labelsByIri,
    mappings,
    selectedClasses,
    suggestions,
  });

  const mappingSuggestions: MappingSuggestion[] = [];

  Object.entries(classSuggestions).forEach(([iriKey, suggestionGroup]) => {
    const { iri: classIri, label: classLabel } = suggestionGroup;
    const attributeSuggestion = [
      ...(attributeSuggestions[iriKey]?.suggestions || []),
    ].sort(alphaSort(({ label, field }) => label || field));
    const relationshipSuggestion = [
      ...(relationshipSuggestions[iriKey]?.suggestions || []),
    ].sort(
      alphaSort(
        ({ label, fields }) =>
          label || fields.map((field) => field.value).join('')
      )
    );

    suggestionGroup.suggestions.forEach(({ source, iri, resource }) => {
      const sourceKey = getResourceSuggestionKey(resource.id, source);

      const allPrimaryIdentifierSuggestions = [
        ...(primaryIdentifierSuggestions[sourceKey]?.suggestions || []),
      ].sort(numericSort((item) => item.score, true));

      const matchedPrimaryIdentifier = findBestPrimaryIdentifierMatch(
        allPrimaryIdentifierSuggestions,
        iri
      );

      const primaryIdentifierSuggestion = matchedPrimaryIdentifier
        ? [matchedPrimaryIdentifier]
        : [];

      const allLabelSuggestions = [
        ...(labelSuggestions[sourceKey]?.suggestions || []),
      ].sort(numericSort((item) => item.score, true));

      const matchedLabel = findBestLabelMatch(allLabelSuggestions, iri);

      const labelSuggestion = matchedLabel ? [matchedLabel] : [];

      const allSuggestions = [
        ...primaryIdentifierSuggestion,
        ...labelSuggestion,
        ...relationshipSuggestion,
        ...attributeSuggestion,
      ];

      const suggestion = {
        name: `${resource.name} - ${classLabel}`,
        resourceId: resource.id,
        classIri,
        classLabel,
        isClassAutoCreated,
        isExpanded: false,
        suggestions: allSuggestions.map((suggestion) => ({
          ...suggestion,
          rejected: false,
          selected: false,
        })),
      };

      mappingSuggestions.push(suggestion);
    });
  });

  return {
    ignoredSuggestions,
    mappingSuggestions: mappingSuggestions.sort(
      alphaSort((suggestion) => suggestion.name)
    ),
  };
};
